import { breakpoints, searchService, subscribeToResize } from '@msc-b2c/shared-utils';

const TEXT_LINE_CLAMP = 4;

export class CruiseItinerary {
  private readonly cruiseItinerary: HTMLElement;
  private readonly innerText!: HTMLElement;
  private readonly readMoreButton!: HTMLButtonElement | null;
  private readonly imageContainer!: Element;
  private readonly dottedImage!: Element;
  private resizeTimeout = 0; // La variabile è inferita come number
  private cleanupResizeSubscription?: () => void;

  constructor(_cruiseItinerary: HTMLElement) {
    this.cruiseItinerary = _cruiseItinerary;

    try {
      const _cruiseContainer: HTMLElement | null =
        this.cruiseItinerary.querySelector('.cmp-container');
      const _innerContainer: HTMLElement | null | undefined = _cruiseContainer?.querySelector(
        '.container > .cmp-container'
      );
      const _innerText: HTMLElement | null | undefined =
        _innerContainer?.querySelector('.text > .cmp-text');
      const _readMoreButton: HTMLButtonElement | null =
        _innerContainer?.querySelector('button.cmp-button') || null;

      const _imageContainer: Element | null | undefined =
        _cruiseContainer?.querySelector('.-dot-line-container');
      const _dottedImage: Element | null | undefined = _cruiseContainer?.querySelector('.image');

      if (!_innerText || !_imageContainer || !_dottedImage) {
        throw new Error('Cruise Itinerary is not initialized correctly');
      }

      this.innerText = _innerText;
      this.readMoreButton = _readMoreButton;
      this.imageContainer = _imageContainer;
      this.dottedImage = _dottedImage;

      //TODO: remove this class from the html structure
      this.innerText.classList.remove('multiline-clamp');
    } catch (error) {
      console.error('Error:', error);
    }
  }

  /**
   * Determine if the read more button should be displayed or not
   * @param {number} maxLines The maximum number of lines of text to be displayed
   */
  private displayReadMoreButton(maxLines: number) {
    const shouldBeDisplayed = this.getLineCount(this.innerText) > maxLines;
    if (shouldBeDisplayed) {
      this.innerText?.classList.add('multiline-clamp');
      if (this.readMoreButton) this.readMoreButton.parentElement?.classList.remove('hide');
    } else {
      if (this.readMoreButton) this.readMoreButton?.parentElement?.classList.add('hide');
    }
  }

  /**
   * Set dotted line image as background so as to make the repeat effect
   */
  private setDottedLineAsBackgound() {
    if (this.imageContainer && this.dottedImage) {
      this.dottedImage.classList.add('hide');
      const imagePath = this.dottedImage.querySelector('.cmp-image > img')?.getAttribute('src');
      if (imagePath) {
        (this.imageContainer as HTMLElement).style.backgroundImage = `url(${imagePath})`;
      }
    }
  }

  /**
   * Given an element, calculate the number of lines of text
   * @param {HTMLElement} element
   * @returns {number} The number of lines of text in the element
   */
  private getLineCount(element: HTMLElement): number {
    let lineHeight = 1;
    let scrollHeight = 0;
    if (element instanceof HTMLElement) {
      const computedStyle = getComputedStyle(element);
      lineHeight = parseFloat(computedStyle.lineHeight);
      scrollHeight = element.scrollHeight;
    }
    return Math.round(scrollHeight / lineHeight);
  }

  // helper function to manage addition and removal of css classes
  private modifyClassList(
    operation: string,
    cssClass: [string, string],
    element: Element | null | undefined
  ) {
    if (operation === 'add') {
      element?.classList.add(cssClass[0]);
    } else if (operation === 'remove') {
      element?.classList.remove(cssClass[0]);
    } else if (operation === 'add-remove') {
      element?.classList.add(cssClass[0]);
      element?.classList.remove(cssClass[1]);
    } else if (operation === 'remove-add') {
      element?.classList.remove(cssClass[0]);
      element?.classList.add(cssClass[1]);
    }
  }

  // helper function to expand the text based on onclick event
  private openInnerText(multiLineClampDelay: number) {
    const thisButton = this.readMoreButton;
    if (thisButton && thisButton !== null) {
      thisButton.addEventListener('click', (e: Event) => {
        e.stopImmediatePropagation();
        const textExpanded = this.innerText.classList.contains('text-expanded');
        thisButton.disabled = true;
        if (textExpanded) {
          this.modifyClassList('remove', ['text-expanded', ''], this.innerText);
          requestAnimationFrame(() => {
            setTimeout(() => {
              this.modifyClassList('add', ['multiline-clamp', ''], this.innerText);
              this.changeReadMoreButtonNameAndIcon(false);
              thisButton.disabled = false;
            }, multiLineClampDelay);

            const isMobile = window.innerWidth <= breakpoints.md;
            if (isMobile)
              this.cruiseItinerary.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        } else {
          //smooth transition and ensure that it does not happen too fast
          requestAnimationFrame(() => {
            this.modifyClassList(
              'add-remove',
              ['text-expanded', 'multiline-clamp'],
              this.innerText
            );
            this.changeReadMoreButtonNameAndIcon(true);
            if (this.readMoreButton) this.readMoreButton.disabled = false;
          });
        }
      });
    }
  }

  // helper function to change read more text and icon when text is expanded or not
  private changeReadMoreButtonNameAndIcon(isOpen: boolean) {
    const thisButton = this.readMoreButton;
    if (!thisButton) return;
    const icon = thisButton.querySelector('.cmp-button__icon');
    const buttonReadMLinkTextCmp: HTMLParagraphElement | null =
      thisButton.querySelector('.cmp-button__text');
    const buttonReadLLinkTextCmp: HTMLParagraphElement | null =
      thisButton.querySelector('.cmp-button__alt-text');
    const readMoreText = buttonReadMLinkTextCmp?.textContent;
    const buttonAttributes = (thisButton as HTMLElement)?.dataset;

    if (!buttonReadMLinkTextCmp || !buttonReadLLinkTextCmp || !buttonAttributes || !readMoreText)
      return;

    if (isOpen) {
      buttonReadLLinkTextCmp.textContent = readMoreText;
      buttonReadMLinkTextCmp.textContent = buttonAttributes['altText'] ?? '';
      this.modifyClassList('remove-add', ['cmp-button__icon--down', 'cmp-button__icon--up'], icon);
    } else {
      buttonReadMLinkTextCmp.textContent = buttonReadLLinkTextCmp.textContent;
      this.modifyClassList('add-remove', ['cmp-button__icon--down', 'cmp-button__icon--up'], icon);
    }
  }

  private async performSearchWithRetry(
    retries = 3,
    timeout = 5000
  ): Promise<{ portCode: string | null; searchResult: any }> {
    const portCode = this.cruiseItinerary.getAttribute('data-port-code');
    if (!portCode) {
      console.error('Port code not found');
      return { portCode: null, searchResult: null };
    }

    const searchParams: any = {
      hitsPerPage: 0,
      filters: `embkPort.key:${portCode}`,
      facets: ['embkPort.key'],
      clickAnalytics: true,
    };

    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const performSearchWithTimeout = async (): Promise<any> => {
      const controller = new AbortController();
      const signal = controller.signal;

      const searchPromise = searchService.performSearchItineraries(searchParams);

      const timeoutPromise = new Promise<never>((_, reject) => {
        const timeoutId = setTimeout(() => {
          controller.abort();
          reject(new Error('Request timeout'));
        }, timeout);

        signal.addEventListener('abort', () => clearTimeout(timeoutId));
      });

      return Promise.race([searchPromise, timeoutPromise]);
    };

    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const result = await performSearchWithTimeout();
        return { portCode, searchResult: result };
      } catch (err: unknown) {
        const isAbort = err instanceof DOMException && err.name === 'AbortError';
        const message = err instanceof Error ? err.message : String(err);

        console.warn(
          isAbort
            ? `Request was aborted on attempt ${attempt}`
            : `Attempt ${attempt} failed: ${message}`
        );

        if (attempt === retries) {
          console.error('Max retries reached, failing');
          return { portCode: null, searchResult: null };
        }

        const backoff = 1000 * Math.pow(2, attempt);
        console.log(`Retrying in ${backoff / 1000}s...`);
        await delay(backoff);
      }
    }

    return { portCode: null, searchResult: null };
  }

  private async departingFunctionality() {
    try {
      const { portCode, searchResult } = await this.performSearchWithRetry();
      // Check if the port code has any associated results
      if (
        !portCode ||
        !searchResult?.facets['embkPort.key'] ||
        searchResult.facets['embkPort.key'][portCode] <= 0
      ) {
        // If the port code is not found in the response, hide the 'cruiseItinerary' element
        this.cruiseItinerary?.closest('.cmp-container')?.remove();
        document.getElementById('departing-cruise-description')?.remove();
      } else {
        const titleDiv = this.cruiseItinerary?.querySelector('.cmp-title__text.hide');
        const departingEl = document.querySelector<HTMLElement>('.cruiseitinerary.departing');
        departingEl?.classList.remove('hide');

        if (titleDiv) {
          const text = titleDiv.innerHTML.trim();
          const heading = titleDiv.getAttribute('data-heading');
          if (heading) {
            // Creation of <h2> element
            const title = document.createElement(heading);
            title.className = 'cmp-title__text'; // Keeps the original class
            title.innerHTML = text; // Set the inner HTML to the original text
            // Change div with h2
            titleDiv?.parentNode?.replaceChild(title, titleDiv);
            titleDiv?.removeAttribute('heading');
            titleDiv?.classList.remove('hide');
          }
        }
      }
    } catch (error) {
      console.error('Search failed:', error);
    }
  }
  private populateDescription(type: 'visiting' | 'departing') {
    const description = this.cruiseItinerary.querySelector('.cruises-itinerary-description');
    const displayCFDescription =
      this.cruiseItinerary.getAttribute('data-display-cf-description') ?? 'false';
    if (!JSON.parse(displayCFDescription)) {
      const cruiseDescriptionComponent = document.querySelector(`#${type}-cruise-description`);
      const itineraryDescription = cruiseDescriptionComponent?.querySelector(
        '.cruises-itinerary-description'
      );
      if (itineraryDescription && description) {
        const cruiseDescription = itineraryDescription.getAttribute('data-cruise-itinerary-text');
        if (cruiseDescription) {
          const descriptionDiv = document.createElement('div');
          descriptionDiv.innerHTML = cruiseDescription; //Used innerHTML to maintain all HTML tags
          description.appendChild(descriptionDiv);
        }
      }
    }
  }

  init() {
    const multiLineClampDelay = 500;
    this.openInnerText(multiLineClampDelay);
    setTimeout(() => {
      this.displayReadMoreButton(TEXT_LINE_CLAMP);
    }, 100);

    this.cleanupResizeSubscription = subscribeToResize(() => {
      if (!this.cruiseItinerary.isConnected) {
        this.cleanupResizeSubscription?.();
        this.cleanupResizeSubscription = undefined;
        return;
      }
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = window.setTimeout(() => {
        if (this.innerText?.classList.contains('text-expanded')) {
          return;
        }
        this.displayReadMoreButton(TEXT_LINE_CLAMP);
      }, 200);
    });

    this.setDottedLineAsBackgound();

    if (this.cruiseItinerary.classList.contains('departing')) {
      this.departingFunctionality().then(() => this.populateDescription('departing'));
    }

    if (this.cruiseItinerary.classList.contains('visiting')) {
      this.populateDescription('visiting');
    }
  }
}

function onDocumentReady(): void {
  document.querySelectorAll('.cruiseitinerary').forEach((node) => {
    const cruiseItinerary = new CruiseItinerary(node as HTMLElement);
    cruiseItinerary.init();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', onDocumentReady);
} else {
  onDocumentReady();
}
